'use client'

import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import { useClientStore } from '@repo/store/useClientStore'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useState } from 'react'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const ClientSelect = ({
  testData = null,
  value = null,
  onChange = (val) => {
    console.log(val)
  },
  sx,
  size = 'small',
  required,
  errors = {},
  name = 'matterNo',
  label = null
}) => {
  const { searchClients, clientSearchResults, fetchingClientSearchResults } = useClientStore()
  const { existingClient } = useNewClientStore()
  const [options, setOptions] = useState(testData || clientSearchResults || [])
  const [selectedValue, setSelectedValue] = useState(value)
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  useEffect(() => {
    if (testData) return
    setOptions(clientSearchResults || [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSearchResults])

  useEffect(() => {
    if (testData || !debouncedSearchTerm) return
    searchClients({ searchCriteria: debouncedSearchTerm })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm])

  useEffect(() => {
    if (existingClient) {
      setSelectedValue(existingClient)
    } else {
      setSelectedValue(value)
    }
  }, [value, existingClient])

  return (
    <div data-testid="ClientSelect" style={{ width: sx?.width || '100%', maxWidth: sx?.maxWidth || '100%' }}>
      <ReactSelectMenu
        label={label ?? 'Client'}
        onChange={(newVal) => {
          onChange(newVal)
          setSelectedValue(newVal)
        }}
        labelKey="searchTerm"
        valueKey="clientNo"
        items={options}
        loading={fetchingClientSearchResults}
        value={selectedValue}
        searchable
        showSearchPrompt={!searchTerm}
        name={name}
        size={size}
        required={required}
        errors={errors}
        onSearchTermChange={setSearchTerm}
        valueCustomRenderFunction={(option) => renderClientValue(option)}
        optionCustomRenderFunction={(option) => renderClientOption(option)}
        sx={{ ...sx }}
      />
    </div>
  )
}

const renderClientValue = ({ clientNo, clientName, forename, surname }) => {
  return (
    <MUIStack direction="row" gap={2}>
      <MUITypography variant="body1" sx={{ mb: 0, fontWeight: 600, pointEvents: 'none' }} noWrap>
        {clientNo}
      </MUITypography>
      <MUITypography variant="body1" sx={{ mb: 0, color: 'text.secondary', pointEvents: 'none' }}>
        |
      </MUITypography>
      <MUITypography noWrap variant="body1" sx={{ mb: 0, color: 'text.secondary', pointEvents: 'none' }}>
        {forename || surname ? forename + ' ' + surname : clientName}
      </MUITypography>
    </MUIStack>
  )
}

const renderClientOption = ({ clientNo, clientName, emailAddress, forename, surname }) => {
  return (
    <MUIStack direction="column" gap={0}>
      <ClientMatterDetailsPill clientNo={clientNo} sx={{ width: 'auto', marginBottom: 0, marginTop: 4 }} />
      <MUITypography
        variant="body1"
        sx={{ mb: 0, mt: 0, color: 'text.secondary', pointEvents: 'none' }}
        noWrap
      >{`${forename || surname ? `${forename} ${surname}` : clientName}${emailAddress ? ` | ${emailAddress}` : ''}`}</MUITypography>
    </MUIStack>
  )
}

ClientSelect.propTypes = {
  testData: PropTypes.array,
  value: PropTypes.object,
  onChange: PropTypes.func,
  sx: PropTypes.object,
  size: PropTypes.string,
  required: PropTypes.bool,
  errors: PropTypes.object,
  name: PropTypes.string,
  label: PropTypes.string
}
